// noinspection TypeScriptValidateJSTypes

import * as scanApiModule from './scanApi';
import { ScanType, SessionDto } from './scanApi';

const { scanApi } = scanApiModule;

export const currentScanTypes: ScanType[] = ['BodyScan', 'FootScan', 'WellnessScan', 'BodyCompositionScan', 'HandScan', 'HeadScan', 'MotionScan'];

export const getScanIdMap = (scanSession: SessionDto) => {
    const body = scanSession.scans?.find((s) => s.scanType === 'BodyScan') ?? null;
    const head = scanSession.scans?.find((s) => s.scanType === 'HeadScan') ?? null;
    const feet = scanSession.scans?.find((s) => s.scanType === 'FootScan') ?? null;
    const hand = scanSession.scans?.find((s) => s.scanType === 'HandScan') ?? null;
    const wellness = scanSession.scans?.find((s) => s.scanType === 'WellnessScan') ?? null;
    const bodyComp = scanSession.scans?.find((s) => s.scanType === 'BodyCompositionScan') ?? null;
    const deprecated = scanSession.scans?.find((s) => s.scanType === 'HealthScan') ?? null;
    const motion = scanSession.scans?.find((s) => s.scanType === 'MotionScan') ?? null;

    return {
        ...(body ? { BodyScan: body.id } : {}),
        ...(head ? { HeadScan: head.id } : {}),
        ...(feet ? { FootScan: feet.id } : {}),
        ...(hand ? { HandScan: hand.id } : {}),
        ...(bodyComp ? { BodyCompositionScan: bodyComp.id } : {}),
        ...(wellness ? { WellnessScan: wellness.id } : {}),
        ...(motion ? { MotionScan: motion.id } : {}),
        ...(deprecated ? { HealthScan: deprecated.id } : {}),
    };
};

export const enhancedScanApi = scanApi.enhanceEndpoints({
    addTagTypes: ["Scans", "SizeMatchResults", "Cohort"],
    endpoints: {
        cohortControllerRetrieveCohorts: { providesTags: ["Cohort"] },
        cohortControllerRetrieveCohort: { providesTags: ["Cohort"] },
        cohortControllerAddCohortMembers: { invalidatesTags: ["Cohort"] },
        cohortControllerRemoveCohortMember: { invalidatesTags: ["Cohort"] },
        cohortControllerUpdateCohort: { invalidatesTags: ["Cohort"] },
        cohortControllerRemoveCohort: { invalidatesTags: ["Cohort"] },
        cohortControllerCreateCohort: { invalidatesTags: ["Cohort"] },
        sizeMatchControllerRetrieveSizeMatchRecommendationsCsv: {
            providesTags: ["Scans"],
            query: (queryArg) => ({
                url: `/size-match/${queryArg.merchantId}/scans/${queryArg.sessionId}/csv`,
                responseHandler: (response: { text: () => any; }) => response.text(),
                params: {
                    productCode: queryArg.productCode,
                    productName: queryArg.productName,
                    fit: queryArg.fit,
                    categoryTags: queryArg.categoryTags,
                },
            }),
        },
        sizeMatchControllerRetrieveSizeMatchRecommendationsBulkCsv: {
            query: (queryArg) => ({
                url: `/size-match/bulk-csv`,
                responseHandler: (response: { text: () => any; }) => response.text(),
                params: {
                    productCode: queryArg.productCode,
                    MerchantId: queryArg.merchantId,
                    ScanTitle: queryArg.scanTitle,
                    GarmentType: queryArg.garmentType,
                    from: queryArg.from,
                    to: queryArg.to,
                    Gender: queryArg.gender,
                    CategoryTags: queryArg.categoryTags,
                },
            }),
        },
        sizeMatchControllerRetrieveSizeMatchRecommendations: {
            providesTags: ["SizeMatchResults"],
        },
        sizeMatchControllerRetrieveScanSizeMatchRecommendations: {
            providesTags: ["SizeMatchResults"],
        },
        sizeMatchControllerRetrieveSessionSizeMatchRecommendations: {
            providesTags: ["SizeMatchResults"],
        },
        sizeMatchControllerDeleteSizeMatchResult: {
            invalidatesTags: ["SizeMatchResults"],
        },
        reportsControllerRetrieveScanQualityReport: {
            query: (queryArg) => ({
                url: `/reports/scan-quality-report`,
                params: {
                    From: queryArg["from"],
                    To: queryArg.to,
                    MerchantId: queryArg.merchantId,
                },
                responseHandler: (response: { text: () => any; }) => response.text(),
            }),
        },
        reportsControllerRetrieveMeasurementQualityReport: {
            query: (queryArg) => ({
                url: `/reports/measurement-quality-report`,
                params: {
                    From: queryArg["from"],
                    To: queryArg.to,
                    MerchantId: queryArg.merchantId,
                },
                responseHandler: (response: { text: () => any; }) => response.text(),
            }),
        },
        reportsControllerRetrieveSizeMatchAccuracyReport: {
            query: (queryArg) => ({
                url: `/reports/sizematch-accuracy-report`,
                params: {
                    From: queryArg["from"],
                    To: queryArg.to,
                    MerchantId: queryArg.merchantId,
                },
                responseHandler: (response: { text: () => any; }) => response.text(),
            }),
        },
        scanControllerRetrieveObjDownload: {
            query: (queryArg) => ({
                url: `/scans/${queryArg.scanId}/obj-download`,
                responseHandler: (response: { blob: () => any; }) => response.blob(),
            }),
            keepUnusedDataFor: 0,
        },
        sessionsControllerRetrieveSessions: { providesTags: ["Scans"] },
        sessionsControllerRetrieveSession: { providesTags: ["Scans"] },
        scanControllerRetrieveScan: { providesTags: ["Scans"] },
        scanControllerSubmitQualityReport: { invalidatesTags: ["Scans"] },
        scanControllerRequestIntegration: { invalidatesTags: ["Scans", "SizeMatchResults"] },
        measurementsControllerRetrieveMeasurementsCsv: {
            query: (queryArg) => ({
                url: `/scans/${queryArg.sessionId}/csv`,
                responseHandler: (response: { text: () => any; }) => response.text(),
            }),
        },
    },
});

export * from './scanApi';

export const {
    useCohortControllerRetrieveCohortsQuery,
    useCohortControllerCreateCohortMutation,
    useCohortControllerRetrieveCohortQuery,
    useCohortControllerUpdateCohortMutation,
    useCohortControllerRemoveCohortMutation,
    useCohortControllerAddCohortMembersMutation,
    useCohortControllerRemoveCohortMemberMutation,
    useMeasurementsControllerRetrieveMeasurementAmendmentsQuery,
    useMeasurementsControllerUpdateScanMeasurementMutation,
    useMeasurementsControllerRetrieveScanMeasurementsQuery,
    useSizeMatchControllerAddOrUpdateSizeMatchReportMutation,
    useScanControllerRequestIntegrationMutation,
    useSessionsControllerRetrieveSessionsQuery,
    useSessionsControllerRetrieveSessionQuery,
    useSizeMatchControllerRetrieveScanSizeMatchRecommendationsQuery,
    useSizeMatchControllerRetrieveSizeMatchRecommendationsCsvQuery,
    useScanControllerSubmitQualityReportMutation,
    useLazyMeasurementsControllerRetrieveMeasurementAmendmentsQuery,
    useLazyScanControllerRetrieveScanQuery,
    useLazyScanControllerRetrieveDownloadUrlQuery,
    useLazyScanControllerRetrieveObjDownloadQuery,
    useLazySessionsControllerRetrieveDiagnosticsUrlQuery,
    useLazyScanControllerRetrieveScanSessionTitleQuery,
    useLazyMeasurementsControllerRetrieveScanMeasurementsQuery,
    useLazyMeasurementsControllerRetrieveMeasurementsCsvQuery,
    useLazyMeasurementsControllerRetrieveAllMeasurementsQuery,
    useLazySizeMatchControllerRetrieveSizeMatchRecommendationsCsvQuery,
    useLazySizeMatchControllerRetrieveSizeMatchRecommendationsBulkCsvQuery,
    useLazyReportsControllerRetrieveScanQualityReportQuery,
    useLazyReportsControllerRetrieveMeasurementQualityReportQuery,
    useLazySessionsControllerRetrieveSessionStatusQuery,
    useLazyReportsControllerRetrieveSizeMatchAccuracyReportQuery,
    useLazySizeMatchControllerRetrieveScanSizeMatchRecommendationsQuery,
    useLazySizeMatchControllerRetrieveProductSizeMatchRecommendationsQuery,
} = enhancedScanApi;
